import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Users } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Modal, Empty } from "@/components/ui-bits";
import { Avatar } from "@/components/Avatar";

const GroupForm = ({ group, clients, onClose, onSaved }) => {
  const [name, setName] = useState(group?.name || "");
  const [ids, setIds] = useState(group?.clientIds || []);
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setIds(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { group ? await api.patch(`/groups/${group.id}`, { name: name.trim(), clientIds: ids }) : await api.post("/groups", { name: name.trim(), clientIds: ids }); toast.success(group ? "Group updated" : "Group created"); onSaved(); }
    catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  return (
    <Modal title={group ? "EDIT GROUP" : "NEW GROUP"} sub="Groups let you assign exercises and programs to many clients at once" onClose={onClose} testId="group-form-modal">
      <form onSubmit={save}>
        <label>Group name</label><input required minLength={2} value={name} onChange={(e) => setName(e.target.value)} placeholder="Monday Morning Class, Online Clients…" data-testid="group-name-input" />
        <label>Members · {ids.length} selected</label>
        <div className="pick-list" data-testid="group-member-list">
          {clients.map((c) => (
            <label className={`pick ${ids.includes(c.id) ? "on" : ""}`} key={c.id} data-testid={`group-member-${c.id}`}>
              <input type="checkbox" checked={ids.includes(c.id)} onChange={() => toggle(c.id)} /><Avatar client={c} size={26} radius={7} /><span className="nm">{c.name}</span>
            </label>
          ))}
        </div>
        <div className="mfoot"><button type="button" className="btn g" onClick={onClose}>Cancel</button><button className="btn" disabled={busy} data-testid="group-save-button">{group ? "Save" : "Create group"}</button></div>
      </form>
    </Modal>
  );
};

export const ClientGroups = ({ clients }) => {
  const [groups, setGroups] = useState(null);
  const [modal, setModal] = useState(null);
  const load = useCallback(() => api.get("/groups").then((r) => setGroups(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const remove = async (g) => { if (!window.confirm(`Delete group "${g.name}"? Clients stay, only the grouping goes.`)) return; await api.delete(`/groups/${g.id}`); toast.success("Group deleted"); load(); };
  return (
    <div className="panel fade" data-testid="client-groups-panel">
      <div className="ph"><h3><Users size={15} style={{ verticalAlign: -2, marginRight: 6 }} />Groups · {groups?.length ?? 0} — classes, cohorts, online clients</h3><button className="btn sm" onClick={() => setModal("new")} data-testid="add-group-button">+ New group</button></div>
      {groups && groups.length === 0 && <Empty>No groups yet. Create "Monday Morning Class" or "Online Clients" and bulk-assign exercises from the Library.</Empty>}
      <div className="ex-grid">
        {(groups || []).map((g) => (
          <div className="ex-card" key={g.id} data-testid={`group-card-${g.id}`}>
            <div className="ex-top"><b>{g.name}</b><span className="chip gray num" data-testid={`group-count-${g.id}`}>{g.members.length}</span></div>
            <div className="avatars">{g.members.slice(0, 6).map((m) => <Avatar key={m.id} client={m} size={24} radius={6} />)}{g.members.length > 6 && <span className="ss">+{g.members.length - 6}</span>}</div>
            <div className="ss">{g.members.map((m) => m.name.split(" ")[0]).join(", ") || "No members yet"}</div>
            <div className="actions" style={{ marginTop: "auto" }}>
              <button className="btn sm g" onClick={() => setModal(g)} data-testid={`group-edit-${g.id}`}>Edit</button>
              <button className="btn sm danger" onClick={() => remove(g)} data-testid={`group-delete-${g.id}`}>Delete</button>
            </div>
          </div>
        ))}
      </div>
      {modal && <GroupForm group={modal === "new" ? null : modal} clients={clients} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
};
